import React from 'react';
import Container from './Container.jsx';
import './Section.css';

/**
 * Section component with ArbiWin design system integration
 * Semantic section wrapper for content organization
 *
 * @param {Object} props - Section properties
 * @param {React.ReactNode} props.children - Section content
 * @param {string} props.variant - Section variant: 'default', 'hero', 'feature', 'testimonial', 'cta'
 * @param {string} props.background - Background variant: 'default', 'surface', 'primary', 'gradient'
 * @param {string} props.padding - Padding size: 'none', 'small', 'medium', 'large', 'xlarge'
 * @param {boolean} props.contained - Wrap content in Container component
 * @param {string} props.containerSize - Container size when contained
 * @param {boolean} props.centered - Center section content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {string} props.id - Section ID for navigation
 * @returns {JSX.Element} Section component
 */
const Section = function Section({
  children,
  variant = 'default',
  background = 'default',
  padding = 'large',
  contained = true,
  containerSize = 'large',
  centered = false,
  className = '',
  testId,
  id,
  ...rest
}) {

  const sectionClasses = [
    'arbiwin-section',
    'arbiwin-section--' + variant,
    'arbiwin-section--bg-' + background,
    'arbiwin-section--padding-' + padding,
    centered ? 'arbiwin-section--centered' : '',
    className
  ].filter(Boolean).join(' ');

  const content = contained ? (
    <Container size={containerSize}>
      {children}
    </Container>
  ) : children;

  return (
    <section
      id={id}
      className={sectionClasses}
      data-testid={testId || 'arbiwin-section'}
      {...rest}
    >
      {content}
    </section>
  );
};

/**
 * HeroSection component - Large header section for landing pages
 *
 * @param {Object} props - HeroSection properties
 * @param {string} props.title - Hero title
 * @param {string} props.subtitle - Hero subtitle
 * @param {React.ReactNode} props.actions - Hero action buttons
 * @param {React.ReactNode} props.children - Additional hero content
 * @param {string} props.backgroundImage - Background image URL
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} HeroSection component
 */
const HeroSection = function HeroSection({
  title,
  subtitle,
  actions,
  children,
  backgroundImage,
  className = ''
}) {

  const heroStyle = backgroundImage ? {
    backgroundImage: `linear-gradient(rgba(18, 18, 18, 0.6), rgba(18, 18, 18, 0.9)), url("${backgroundImage}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  } : {};

  return (
    <Section
      variant="hero"
      background="gradient"
      padding="xlarge"
      centered
      className={'arbiwin-hero-section ' + className}
      style={heroStyle}
    >
      <div className="arbiwin-hero-content">
        {title && (
          <h1 className="arbiwin-hero-title">
            {title}
          </h1>
        )}
        {subtitle && (
          <p className="arbiwin-hero-subtitle">
            {subtitle}
          </p>
        )}
        {children}
        {actions && (
          <div className="arbiwin-hero-actions">
            {actions}
          </div>
        )}
      </div>
    </Section>
  );
};

/**
 * FeatureSection component - Section for showcasing features
 *
 * @param {Object} props - FeatureSection properties
 * @param {string} props.title - Section title
 * @param {string} props.subtitle - Section subtitle
 * @param {React.ReactNode} props.children - Feature content
 * @param {string} props.layout - Layout: 'grid', 'list', 'columns'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FeatureSection component
 */
const FeatureSection = function FeatureSection({
  title,
  subtitle,
  children,
  layout = 'grid',
  className = ''
}) {
  return (
    <Section
      variant="feature"
      background="surface"
      className={'arbiwin-feature-section arbiwin-feature-section--' + layout + ' ' + className}
    >
      {(title || subtitle) && (
        <div className="arbiwin-section-header">
          {title && (
            <h2 className="arbiwin-section-title">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="arbiwin-section-subtitle">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className="arbiwin-feature-content">
        {children}
      </div>
    </Section>
  );
};

/**
 * CTASection component - Call-to-action section
 *
 * @param {Object} props - CTASection properties
 * @param {string} props.title - CTA title
 * @param {string} props.message - CTA message
 * @param {React.ReactNode} props.actions - CTA buttons
 * @param {React.ReactNode} props.children - Additional CTA content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} CTASection component
 */
const CTASection = function CTASection({
  title,
  message,
  actions,
  children,
  className = ''
}) {
  return (
    <Section
      variant="cta"
      background="primary"
      padding="large"
      centered
      className={'arbiwin-cta-section ' + className}
    >
      <div className="arbiwin-cta-content">
        {title && (
          <h2 className="arbiwin-cta-title">
            {title}
          </h2>
        )}
        {message && (
          <p className="arbiwin-cta-message">
            {message}
          </p>
        )}
        {children}
        {actions && (
          <div className="arbiwin-cta-actions">
            {actions}
          </div>
        )}
      </div>
    </Section>
  );
};

/**
 * ContentSection component - General content section with header
 *
 * @param {Object} props - ContentSection properties
 * @param {string} props.title - Section title
 * @param {string} props.subtitle - Section subtitle
 * @param {React.ReactNode} props.children - Section content
 * @param {boolean} props.headerCentered - Center the header
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ContentSection component
 */
const ContentSection = function ContentSection({
  title,
  subtitle,
  children,
  headerCentered = false,
  className = '',
  ...rest
}) {
  return (
    <Section
      variant="default"
      className={'arbiwin-content-section ' + className}
      {...rest}
    >
      {(title || subtitle) && (
        <div className={[
          'arbiwin-section-header',
          headerCentered ? 'arbiwin-section-header--centered' : ''
        ].filter(Boolean).join(' ')}>
          {title && (
            <h2 className="arbiwin-section-title">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="arbiwin-section-subtitle">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className="arbiwin-section-content">
        {children}
      </div>
    </Section>
  );
};

/**
 * FooterSection component - Footer content section
 * Based on header_and_footer.html footer structure
 *
 * @param {Object} props - FooterSection properties
 * @param {React.ReactNode} props.children - Footer content
 * @param {string} props.copyright - Copyright text
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FooterSection component
 */
const FooterSection = function FooterSection({
  children,
  copyright,
  className = ''
}) {
  return (
    <Section
      variant="default"
      background="surface"
      padding="large"
      contained={false}
      className={'arbiwin-footer-section ' + className}
      as="footer"
    >
      <Container size="full" padding="large">
        <div className="arbiwin-footer-content">
          {children}
        </div>
        {copyright && (
          <div className="arbiwin-footer-copyright">
            <p>{copyright}</p>
          </div>
        )}
      </Container>
    </Section>
  );
};

Section.displayName = 'Section';
HeroSection.displayName = 'HeroSection';
FeatureSection.displayName = 'FeatureSection';
CTASection.displayName = 'CTASection';
ContentSection.displayName = 'ContentSection';
FooterSection.displayName = 'FooterSection';

export default Section;
export {
  HeroSection,
  FeatureSection,
  CTASection,
  ContentSection,
  FooterSection
};
